export interface ManualPromiseCallbacks<T> {
    resolve: (value: T | PromiseLike<T>) => void
    reject: (reason?: any) => void
}

/**
 * Wrapper around "new Promise()" which stores (and exposes) resolve and reject methods of internal promise.
 *
 * It also allows to store data in the promise.
 */
export class ManualPromise<TResult = void, TData = never> implements Promise<TResult> {
    private _promise!: Promise<TResult>
    private _callbacks?: ManualPromiseCallbacks<TResult>

    constructor(public readonly data?: TData) {
        this.resolve = this.resolve.bind(this)
        this.reject = this.reject.bind(this)
        this.reset = this.reset.bind(this)

        this.then = this.then.bind(this)
        this.catch = this.catch.bind(this)
        this.finally = this.finally.bind(this)

        this.createInternalPromise()
    }

    private createInternalPromise() {
        this._promise = new Promise((resolve, reject) => {
            this._callbacks = {
                resolve,
                reject
            }
        })
    }

    public get settled(): boolean {
        return this._callbacks === undefined
    }

    /**
     * Gets object with just internal callbacks.
     *
     * Useful if you want to pass something which can be used to resolve/reject the promise but not await it.
     */
    public get callbacks(): ManualPromiseCallbacks<TResult> | undefined {
        return this._callbacks
    }

    /**
     * Resolves internal promise with provided value.
     * @param value Final value of the promise.
     */
    public resolve(value: TResult | PromiseLike<TResult>) {
        this.callbacks?.resolve(value)
        this.cleanup()
    }

    /**
     * Rejects internal promise with provided reason.
     * @param reason Reason for rejecting promise
     */
    public reject(reason?: any) {
        this.callbacks?.reject(reason)
        this.cleanup()
    }

    private cleanup() {
        this._callbacks = undefined
    }

    /**
     * Resets the manual promise.
     *
     * Promises awaited before reset will still get resolved/rejected after the reset.
     */
    public reset() {
        const oldCallbacks = this.callbacks
        this.createInternalPromise()
        if (oldCallbacks) {
            this.then(oldCallbacks.resolve, oldCallbacks.reject)
        }
    }

    // Implementation of Promise<T> interface
    public [Symbol.toStringTag] = 'ManualPromise'

    public then<TFulfilled = TResult, TRejected = never>(
        onFulfilled?: ((value: TResult) => TFulfilled | PromiseLike<TFulfilled>) | null | undefined,
        onRejected?: ((reason: any) => TRejected | PromiseLike<TRejected>) | null | undefined
    ): Promise<TFulfilled | TRejected> {
        return this._promise.then(onFulfilled, onRejected)
    }

    public catch<TRejected = never>(
        onRejected?: ((reason: any) => TRejected | PromiseLike<TRejected>) | null | undefined
    ): Promise<TResult | TRejected> {
        return this._promise.catch(onRejected)
    }

    public finally(onFinally?: (() => void) | null | undefined): Promise<TResult> {
        return this._promise.finally(onFinally)
    }
}
